import {DestroyRef, inject, Injectable} from '@angular/core';
import {Action, ActionType, NgxsOnInit, Selector, State, StateContext, Store} from '@ngxs/store';
import {
  AreaOfInterest,
  AssetLocation,
  LatLng,
  MapState,
  MapStateModel,
  MapStop,
  MapViewport,
  MapZoomLevel,
  ReplayConfig,
  ReplayState
} from './model';
import {catchError, map, Observable, of, switchMap, tap, throwError, withLatestFrom} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import * as MapActions from './actions';
import {WINDOW_TOKEN} from '../../../core/tokens/window.token';
import {AssetService} from '../../asset.service';
import {endOfDay, startOfDay} from 'date-fns';

const now = new Date();
const today = startOfDay(now);

@State<MapStateModel>({
  name: 'assetDetailsMapState',
  defaults: {
    currentMode: "live",
    assetId: '',
    tripId: undefined,
    replayConfig: {
      startTime: today.valueOf(),
      endTime: now.valueOf(),
      speed: 1,
      isPaused: true,
      currentIndex: 0,
      lastTimestamp: 0
    },
    viewport: {
      center: {lat: 0, lng: 0},
      zoom: {level: 16, autoZoom: true}
    },
    currentLocation: null,
    pathHistory: [],
    stops: [],
    areasOfInterest: [],

    historicalLocations: [],

    replayState: {
      isPaused: true,
      currentTime: 0,
      totalTime: 0,
      progress: 0
    },

    isLoading: {
      pathHistory: false,
      stops: false,
      areas: false,
      locationHistory: false,
      liveMode: false,
    },

    error: null
  }
})
@Injectable()
export class AssetDetailsMapState implements NgxsOnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly assetService = inject(AssetService);
  private readonly window = inject(WINDOW_TOKEN);
  private replayIntervalId: number | null = null;

  @Selector()
  static currentMode(state: MapStateModel): MapState {
    return state.currentMode;
  }

  @Selector()
  static assetId(state: MapStateModel): string {
    return state.assetId;
  }

  @Selector()
  static currentLocation(state: MapStateModel): AssetLocation | null {
    return state.currentLocation;
  }

  @Selector()
  static center(state: MapStateModel): LatLng {
    return state.viewport.center;
  }

  @Selector()
  static zoom(state: MapStateModel): MapZoomLevel {
    return state.viewport.zoom;
  }

  @Selector()
  static viewport(state: MapStateModel): MapViewport {
    return state.viewport;
  }

  @Selector()
  static pathHistory(state: MapStateModel): LatLng[] {
    return state.pathHistory;
  }

  @Selector()
  static stops(state: MapStateModel): MapStop[] {
    return state.stops;
  }

  @Selector()
  static areasOfInterest(state: MapStateModel): AreaOfInterest[] {
    return state.areasOfInterest;
  }

  @Selector()
  static replayState(state: MapStateModel): ReplayState {
    return state.replayState;
  }

  @Selector()
  static replayConfig(state: MapStateModel) {
    return state.replayConfig;
  }

  @Selector([AssetDetailsMapState.replayConfig])
  static replayDates(replayConfig: ReplayConfig) {
    return {
      startDate: replayConfig.startTime,
      endDate: replayConfig.endTime,
    };
  }

  @Selector([AssetDetailsMapState.replayConfig])
  static replaySpeed(state: ReplayConfig) {
    return state.speed;
  }

  @Selector([AssetDetailsMapState.replayConfig])
  static isReplayPaused(state: ReplayConfig): boolean {
    return state.isPaused;
  }

  @Selector()
  static isLoading(state: MapStateModel): boolean {
    const {pathHistory, stops, areas, locationHistory, liveMode} = state.isLoading;
    return pathHistory || stops || areas || locationHistory || liveMode;
  }

  @Selector()
  static error(state: MapStateModel): string | null {
    return state.error;
  }

  @Selector()
  static historicalLocations(state: MapStateModel): AssetLocation[] {
    return state.historicalLocations;
  }

  @Selector([
    AssetDetailsMapState.replayConfig,
    AssetDetailsMapState.currentMode,
    AssetDetailsMapState.historicalLocations
  ])
  static historyPath(replayState: ReplayConfig, currentMode: 'live' | 'replay' | 'trip', historicalLocations: AssetLocation[]): {
    back: LatLng[],
    forward: LatLng[]
  } {
    if (currentMode !== 'replay') {
      return {
        back: [],
        forward: [],
      };
    }
    const currentIndex = replayState.currentIndex;
    const locations = historicalLocations.map(loc => loc.location);
    const locationsBehindIndex = locations.slice(0, currentIndex + 1);
    const locationsAheadOfIndex = locations.slice(currentIndex);
    return {
      back: locationsBehindIndex,
      forward: locationsAheadOfIndex,
    }
  }

  @Selector([
    AssetDetailsMapState.replayConfig,
    AssetDetailsMapState.historicalLocations
  ])
  static historyLocationStat({currentIndex}: ReplayConfig, historicalLocations: AssetLocation[]) {
    if (!historicalLocations.length || historicalLocations[currentIndex] === undefined) {
      return {
        location: {
          lat: 0,
          lng: 0,
        },
        address: 'Unknown location',
        currentSpeed: 0,
      }
    }
    const {location, speed, address} = historicalLocations[currentIndex];

    return {
      location,
      address,
      currentSpeed: speed ?? 0,
    }
  }

  ngxsOnInit(ctx: StateContext<MapStateModel>): void {
    this.setupAutoFollowBehavior(ctx);
  }

  @Action(MapActions.SetMapMode)
  setMapMode(ctx: StateContext<MapStateModel>, action: MapActions.SetMapMode) {
    // Clean up existing resources first
    this.cleanupResources();
    // Update state
    ctx.patchState({
      currentMode: action.mode,
      assetId: action.assetId,
      tripId: action.tripId,
      replayConfig: {
        ...ctx.getState().replayConfig,
        startTime: action.replayStartTime,
        endTime: action.replayEndTime,
        speed: action.replaySpeed || 1,
        isPaused: true,
        currentIndex: 0,
        lastTimestamp: Date.now()
      }
    });

    // Setup mode-specific resources
    switch (action.mode) {
      case 'replay':
        return this.setupReplayMode(ctx);

      case 'live':
        return this.setupLiveMode(ctx);

      case 'trip':
        return this.setupTripMode(ctx);

      default:
        return of();
    }
  }

  @Action(MapActions.UpdateViewport)
  updateViewport(ctx: StateContext<MapStateModel>, action: MapActions.UpdateViewport): void {
    ctx.patchState({
      viewport: {
        ...ctx.getState().viewport,
        ...action.viewport
      }
    });
  }

  @Action(MapActions.ToggleAutoFollow)
  toggleAutoFollow(ctx: StateContext<MapStateModel>, action: MapActions.ToggleAutoFollow): void {
    const state = ctx.getState();
    ctx.patchState({
      viewport: {
        ...state.viewport,
        zoom: {
          ...state.viewport.zoom,
          autoZoom: action.enabled
        }
      }
    });
  }

  @Action(MapActions.ControlReplay)
  controlReplay(ctx: StateContext<MapStateModel>, action: MapActions.ControlReplay): void {
    const state = ctx.getState();

    if (state.currentMode !== 'replay') {
      return;
    }

    switch (action.action) {
      case 'play':
        // Start or resume replay
        ctx.patchState({
          replayConfig: {
            ...state.replayConfig,
            isPaused: false,
            lastTimestamp: Date.now()
          }
        });
        this.startReplayTimer(ctx);
        break;

      case 'pause':
        // Pause replay
        ctx.patchState({
          replayConfig: {
            ...state.replayConfig,
            isPaused: true
          }
        });
        this.stopReplayTimer();
        break;

      case 'reset':
        // Reset to beginning
        this.resetReplay(ctx);
        break;

      case 'seek':
        // Seek to specific time
        if (action.timestamp) {
          this.seekReplay(ctx, action.timestamp);
        }
        break;
    }
  }

  @Action(MapActions.UpdateAssetLocation)
  updateAssetLocation(ctx: StateContext<MapStateModel>, action: MapActions.UpdateAssetLocation): void {
    // Only update the location - other updates will be handled by the subscription in ngxsOnInit
    ctx.patchState({
      currentLocation: action.location
    });
  }

  @Action(MapActions.UpdatePathWithLocation)
  updatePathWithLocation(ctx: StateContext<MapStateModel>, action: MapActions.UpdatePathWithLocation): void {
    this.updatePathHistory(ctx, action.location);
  }

  @Action(MapActions.FetchPathHistory)
  fetchPathHistory(ctx: StateContext<MapStateModel>): Observable<LatLng[]> {
    const state = ctx.getState();

    ctx.patchState({
      isLoading: {
        ...state.isLoading,
        pathHistory: true
      }
    });

    switch (state.currentMode) {
      case 'replay':
        if (state.historicalLocations.length > 0) {
          const pathPoints = state.historicalLocations.map(loc => loc.location);
          ctx.patchState({
            pathHistory: pathPoints,
            isLoading: {
              ...state.isLoading,
              pathHistory: false
            }
          });
          return of(pathPoints);
        }
        return this.fetchReplayPath(ctx);
      case "trip":
        if (state.tripId) {
          return this.fetchTripRoute(ctx);
        }
        return of([]);
      default:
        // For LIVE mode or invalid TRIP mode, clear path history
        ctx.patchState({
          pathHistory: [],
          isLoading: {
            ...state.isLoading,
            pathHistory: false
          }
        });
        return of([]);
    }
  }

  @Action(MapActions.FetchStops)
  fetchStops(ctx: StateContext<MapStateModel>): Observable<MapStop[]> {
    const state = ctx.getState();

    if (state.currentMode !== 'trip' || !state.tripId) {
      ctx.patchState({stops: []});
      return of([]);
    }

    ctx.patchState({
      isLoading: {
        ...state.isLoading,
        stops: true
      }
    });

    return this.assetService.getTripStops(state.tripId).pipe(
      tap(stops => {
        ctx.patchState({
          stops,
          isLoading: {
            ...state.isLoading,
            stops: false
          }
        });
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message,
          isLoading: {
            ...state.isLoading,
            stops: false
          }
        });
        return of([]);
      })
    );
  }

  @Action(MapActions.FetchAreasOfInterest)
  fetchAreasOfInterest(ctx: StateContext<MapStateModel>): Observable<AreaOfInterest[]> {
    const state = ctx.getState();

    ctx.patchState({
      isLoading: {
        ...state.isLoading,
        areas: true
      }
    });

    if (state.currentMode === 'trip' && state.tripId) {
      return this.fetchTripAreas(ctx);
    } else {
      return this.fetchViewportAreas(ctx);
    }
  }

  @Action(MapActions.UpdateReplayDate)
  updateReplayDate(ctx: StateContext<MapStateModel>, action: MapActions.UpdateReplayDate) {
    // Clean up existing resources first
    this.cleanupResources();
    const {date} = action;
    const startDate = startOfDay(date);
    const endDate = endOfDay(date);

    ctx.patchState({
      replayConfig: {
        ...ctx.getState().replayConfig,
        startTime: startDate.valueOf(),
        endTime: endDate.valueOf()
      }
    });

    return this.setupReplayMode(ctx);
  }

  @Action(MapActions.AdvanceReplayIndex)
  advanceReplayIndex(ctx: StateContext<MapStateModel>, action: MapActions.AdvanceReplayIndex): void {
    const state = ctx.getState();

    if (state.currentMode !== 'replay' || state.historicalLocations.length === 0) {
      return;
    }

    const {currentIndex, isPaused} = state.replayConfig;

    if (isPaused || currentIndex >= state.historicalLocations.length - 1) {
      return;
    }

    // Instead of using the timestamp of the current location,
    // we'll track a simulated time for the replay that advances continuously
    // We can use the replayState.currentTime for this
    const startTime = state.replayConfig.startTime || 0;
    const currentSimulationTime = state.replayState.currentTime + startTime;
    const targetTime = currentSimulationTime + action.simulationElapsed;

    let newIndex = currentIndex;
    // Find all locations that should be shown by now
    while (
      newIndex < state.historicalLocations.length - 1 &&
      state.historicalLocations[newIndex + 1].timestamp <= targetTime
      ) {
      newIndex++;
      ctx.dispatch(new MapActions.UpdateAssetLocation(state.historicalLocations[newIndex]));
    }

    // Update the replay state with the new target time
    const totalTime = (state.replayConfig.endTime || 0) - (state.replayConfig.startTime || 0);
    const elapsed = targetTime - (state.replayConfig.startTime || 0);
    const progress = totalTime > 0 ? (elapsed / totalTime) * 100 : 0;

    ctx.patchState({
      replayState: {
        isPaused: state.replayConfig.isPaused,
        currentTime: elapsed,
        totalTime,
        progress
      },
      replayConfig: {
        ...state.replayConfig,
        currentIndex: newIndex,
        lastTimestamp: Date.now()
      }
    });


    // Check if we reached the end
    if (newIndex >= state.historicalLocations.length - 1) {
      ctx.patchState({
        replayConfig: {
          ...state.replayConfig,
          isPaused: true
        }
      });
      this.stopReplayTimer();
    }
  }

  @Action(MapActions.UpdateReplaySpeed)
  updateReplaySpeed(ctx: StateContext<MapStateModel>, action: MapActions.UpdateReplaySpeed) {
    const state = ctx.getState();
    const {speed} = action;
    ctx.patchState({
      replayConfig: {
        ...state.replayConfig,
        speed
      }
    });
  }

  @Action(MapActions.CleanupMapResources)
  cleanupMapResources(): void {
    this.cleanupResources();
  }

  private setupAutoFollowBehavior(ctx: StateContext<MapStateModel>): void {
    this.store.select(AssetDetailsMapState.currentLocation).pipe(
      withLatestFrom(this.store.select(AssetDetailsMapState.viewport)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([location, viewport]) => {
      if (!location || !viewport.zoom.autoZoom) {
        console.warn('Auto-follow is disabled');
        return;
      }

      // Only recenter if the asset is near the edge of the viewport
      if (this.isLocationNearViewportEdge(location.location, viewport)) {
        console.log('Auto-following asset');
        ctx.dispatch(new MapActions.UpdateViewport({
          center: location.location
        }));
      }
    });
  }

  /**
   * Determines if a location is near the edge of the current viewport
   * @param location The asset location to check
   * @param viewport Current map viewport
   * @returns boolean True if the location is near the edge and map should recenter
   */
  private isLocationNearViewportEdge(location: LatLng, viewport: MapViewport): boolean {
    // If bounds aren't available, we can't determine edge proximity
    if (!viewport.bounds) {
      return true; // Default to centering if we can't determine bounds
    }

    const {north, south, east, west} = viewport.bounds;

    // Calculate the viewport dimensions
    const latSpan = north - south;
    const lngSpan = east - west;

    // Define the "edge threshold" - how close to the edge (as a percentage)
    // the location needs to be to trigger recentering
    const edgeThreshold = 0.2; // 20% from any edge

    // Calculate the distance from each edge as a percentage of viewport size
    const distFromNorth = (north - location.lat) / latSpan;
    const distFromSouth = (location.lat - south) / latSpan;
    const distFromEast = (east - location.lng) / lngSpan;
    const distFromWest = (location.lng - west) / lngSpan;

    // Check if the location is too close to any edge
    return (
      distFromNorth < edgeThreshold ||
      distFromSouth < edgeThreshold ||
      distFromEast < edgeThreshold ||
      distFromWest < edgeThreshold
    );
  }

  /**
   * Starts the replay timer for advancing the replay index at specified intervals based on the replay configuration.
   * Clears any existing timer before starting a new one.
   *
   * @param ctx The state context containing the replay configuration and state management methods.
   * @return void
   */
  private startReplayTimer(ctx: StateContext<MapStateModel>): void {
    // Clear any existing interval
    this.stopReplayTimer();

    // Set up a new interval
    this.replayIntervalId = this.window.setInterval(() => {
      const state = ctx.getState();
      if (state.replayConfig.isPaused) {
        return;
      }

      const now = Date.now();
      const elapsed = now - state.replayConfig.lastTimestamp;
      const simulationElapsed = elapsed * state.replayConfig.speed;

      ctx.dispatch(new MapActions.AdvanceReplayIndex(simulationElapsed));
    }, 100);
  }

  /**
   * Stops the replay timer if it is currently active by clearing the interval and resetting the interval ID.
   *
   * @return {void} This method does not return a value.
   */
  private stopReplayTimer(): void {
    if (this.replayIntervalId !== null) {
      this.window.clearInterval(this.replayIntervalId);
      this.replayIntervalId = null;
    }
  }

  private cleanupResources() {
    // Clear replay interval
    this.stopReplayTimer();
  }

  /**
   * Sets up the replay mode by fetching historical locations and configuring the replay state.
   *
   * @param {StateContext<MapStateModel>} ctx - The context object representing the current state and allowing state modifications.
   * @return {Observable<void>} An observable that completes after finishing the setup, or emits an error if any occurs.
   */
  private setupReplayMode(ctx: StateContext<MapStateModel>): Observable<void> {
    const state = ctx.getState();
    const {assetId, replayConfig} = state;

    if (!replayConfig.startTime || !replayConfig.endTime) {
      ctx.patchState({
        error: 'Replay mode requires start and end times'
      });
      return of(undefined);
    }

    ctx.patchState({
      isLoading: {
        ...state.isLoading,
        locationHistory: true,
      }
    })

    // Fetch historical locations
    return this.assetService.getAssetPathLocations(
      assetId,
      new Date(replayConfig.startTime),
      new Date(replayConfig.endTime)
    ).pipe(
      switchMap(locations => {
        if (locations.length === 0) {
          ctx.patchState({
            error: 'No replay data available for the selected time range',
            isLoading: {
              ...state.isLoading,
              locationHistory: false,
            }
          });
          return of(undefined);
        }

        ctx.patchState({
          historicalLocations: locations,
          currentLocation: locations[0],
          replayConfig: {
            ...state.replayConfig,
            currentIndex: 0,
            lastTimestamp: Date.now(),
            startTime: locations[0].timestamp,
            endTime: locations[locations.length - 1].timestamp
          },
          isLoading: {
            ...state.isLoading,
            locationHistory: false,
          }
        });

        // Fetch path history
        return ctx.dispatch(new MapActions.FetchPathHistory());
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message,
          isLoading: {
            ...state.isLoading,
            locationHistory: false,
          }
        });
        return of();
      }),
    );
  }

  /**
   * Configures the live mode by checking and setting up the necessary state values.
   *
   * @param {StateContext<MapStateModel>} ctx - The state context that holds the current state and allows state modification.
   * @return {Observable<void>} An observable that emits the location stream if the setup is successful,
   * or an undefined observable if an error occurs.
   */
  private setupLiveMode(ctx: StateContext<MapStateModel>): Observable<void> {
    const {assetId, isLoading} = ctx.getState();
    if (assetId === '') {
      ctx.patchState({
        error: 'No asset selected',
        isLoading: {
          ...isLoading,
          liveMode: false,
        }
      });
      return of();
    }

    ctx.patchState({
      isLoading: {
        ...isLoading,
        liveMode: true,
      }
    });

    return this.getLocationStream(assetId, 'assets', ctx.dispatch).pipe(
      tap(() => {
        ctx.patchState({
          isLoading: {
            ...isLoading,
            liveMode: false,
          }
        })
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message,
          isLoading: {
            ...isLoading,
            liveMode: false,
          }
        });
        return of();
      })
    );
  }

  /**
   * Configures the application for trip mode based on the current asset ID in the state.
   * If no asset ID is present, an error will be set in the state.
   *
   * @param {StateContext<MapStateModel>} ctx - The state context containing the current state and methods to update it.
   * @return {Observable<void>} An observable that streams location data if an asset ID is present, or `undefined` if no asset ID is found.
   */
  private setupTripMode(ctx: StateContext<MapStateModel>): Observable<void> {
    const {tripId: assetId} = ctx.getState();
    if (!assetId || assetId === '') {
      ctx.patchState({
        error: 'No asset selected'
      });
      return of();
    }

    return this.getLocationStream(assetId, 'trips', ctx.dispatch);
  }

  /**
   * Retrieves a location stream for a specified asset or trip.
   *
   * @param {string} assetId - The unique identifier of the asset for which the location stream is requested.
   * @param {'assets' | 'trips'} scope - Determines whether the location stream pertains to assets or trips.
   * @param {(action: ActionType) => Observable<void>} dispatch - A function to dispatch an action, returning an Observable.
   * @return {Observable<void>} An observable stream that processes and dispatches location updates.
   */
  private getLocationStream(assetId: string, scope: 'assets' | 'trips', dispatch: (action: ActionType) => Observable<void>): Observable<void> {
    return this.assetService.getAssetLocationStream(assetId, scope).pipe(
      switchMap(event => {
          if (event instanceof ErrorEvent) {
            return throwError(() => event);
          }
          return of(event);
        }
      ),
      switchMap(event => {
        return dispatch(
          new MapActions.UpdateAssetLocation(event) as unknown as ActionType);
      })
    );
  }

  /**
   * Resets the replay process by setting the current location to the first historical location,
   * resetting the replay configuration, and reinitializing the path history.
   *
   * @param ctx - The state context of the MapStateModel that provides access to the current state
   *              and allows patching the state.
   * @return void
   */
  private resetReplay(ctx: StateContext<MapStateModel>): void {
    const state = ctx.getState();

    if (state.historicalLocations.length === 0) {
      return;
    }

    ctx.patchState({
      currentLocation: state.historicalLocations[0],
      replayConfig: {
        ...state.replayConfig,
        currentIndex: 0,
        lastTimestamp: Date.now()
      },
      pathHistory: [state.historicalLocations[0].location]
    });
  }

  /**
   * Updates the state to replay the historical location closest to the specified timestamp and maintains the path history up to that point.
   *
   * @param {StateContext<MapStateModel>} ctx - The state context of the map model, used to access and manipulate the application state.
   * @param {number} timestamp - The target timestamp for seeking the closest historical location.
   * @return {void} This method does not return a value but updates the state context.
   */
  private seekReplay(ctx: StateContext<MapStateModel>, timestamp: number): void {
    const state = ctx.getState();

    if (state.historicalLocations.length === 0) {
      return;
    }

    // Find closest location to the requested timestamp
    const targetIndex = state.historicalLocations.findIndex(loc =>
      loc.timestamp >= timestamp
    );

    if (targetIndex >= 0) {
      // Calculate the elapsed time from the start time to the target timestamp
      const startTime = state.replayConfig.startTime || 0;
      const currentTime = timestamp - startTime;
      const totalTime = (state.replayConfig.endTime || 0) - startTime;
      const progress = totalTime > 0 ? (currentTime / totalTime) * 100 : 0;

      ctx.patchState({
        currentLocation: state.historicalLocations[targetIndex],
        replayConfig: {
          ...state.replayConfig,
          currentIndex: targetIndex,
          lastTimestamp: Date.now()
        },
        // Update the replayState with the new current time to ensure the simulation continues from this point
        replayState: {
          isPaused: state.replayConfig.isPaused,
          currentTime,
          totalTime,
          progress
        }
      });

      // Update path history to include all locations up to this point
      const newPathHistory = state.historicalLocations
        .slice(0, targetIndex + 1)
        .map(loc => loc.location);

      ctx.patchState({
        pathHistory: newPathHistory
      });
    }
  }

  /**
   * Updates the path history state in the replay configuration based on the provided location's timestamp.
   * Calculates the progress of the replay and updates the state accordingly.
   *
   * @param {StateContext<MapStateModel>} ctx - The state context representing the current state.
   * @param {AssetLocation} location - The asset location containing the timestamp used to compute the progress.
   * @return {void} No value is returned by this method.
   */
  private updatePathHistory(ctx: StateContext<MapStateModel>, location: AssetLocation): void {
    const state = ctx.getState();
    const startTime = state.replayConfig.startTime || 0;
    const endTime = state.replayConfig.endTime || 0;
    const totalTime = endTime - startTime;

    if (totalTime <= 0) {
      return;
    }

    const currentTime = location.timestamp - startTime;
    const progress = (currentTime / totalTime) * 100;

    ctx.patchState({
      replayState: {
        isPaused: state.replayConfig.isPaused,
        currentTime,
        totalTime,
        progress
      }
    });
  }

  /**
   * Fetches the replay path based on the start and end time provided in the state.
   * Updates the state with the path history, historical locations, and the replay configuration.
   *
   * @param {StateContext<MapStateModel>} ctx - The state context containing the state model
   *                                            which holds necessary configurations and asset information.
   * @return {Observable<LatLng[]>} An observable that emits an array of location strings representing the replay path.
   */
  private fetchReplayPath(ctx: StateContext<MapStateModel>): Observable<LatLng[]> {
    const state = ctx.getState();

    if (!state.replayConfig.startTime || !state.replayConfig.endTime) {
      ctx.patchState({
        isLoading: {
          ...state.isLoading,
          pathHistory: false
        }
      });
      return of([]);
    }

    return this.assetService.getAssetPathLocations(
      state.assetId,
      new Date(state.replayConfig.startTime),
      new Date(state.replayConfig.endTime)
    ).pipe(
      map(locations => {
        ctx.patchState({
          historicalLocations: locations,
          currentLocation: locations.length > 0 ? locations[0] : null,
          replayConfig: {
            ...state.replayConfig,
            currentIndex: 0,
            lastTimestamp: Date.now()
          }
        });
        return locations.map(loc => loc.location);
      })
    );
  }

  /**
   * Fetches the trip route based on the trip ID stored in the state.
   * Updates the state with the path history and loading status.
   *
   * @param {StateContext<MapStateModel>} ctx - The state context of the MapStateModel.
   * @return {Observable<LatLng[]>} An observable emitting an array of location data or an empty array in case of an error.
   */
  private fetchTripRoute(ctx: StateContext<MapStateModel>): Observable<LatLng[]> {
    const state = ctx.getState();

    if (!state.tripId) {
      return of([]);
    }
    return this.assetService.getAssetPathLocations(state.tripId).pipe(
      map(locations => {
        ctx.patchState({
          pathHistory: locations.map(loc => loc.location),
          isLoading: {
            ...state.isLoading,
            pathHistory: false
          }
        });
        return locations.map(loc => loc.location);
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message,
          isLoading: {
            ...state.isLoading,
            pathHistory: false
          }
        });
        return of([]);
      })
    )
  }

  /**
   * Fetches the areas of interest for a specific trip based on the trip ID in the state.
   *
   * @param {StateContext<MapStateModel>} ctx - The state context containing the current state of the map.
   * @return {Observable<AreaOfInterest[]>} An observable that emits the list of areas of interest or an empty list if the trip ID is missing.
   */
  private fetchTripAreas(ctx: StateContext<MapStateModel>): Observable<AreaOfInterest[]> {
    const state = ctx.getState();

    if (!state.tripId) {
      return of([]);
    }
    return this.assetService.getTripAreas(state.tripId).pipe(
      tap(areas => {
        ctx.patchState({
          areasOfInterest: areas,
          isLoading: {
            ...state.isLoading,
            areas: false
          }
        });
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message,
          isLoading: {
            ...state.isLoading,
            areas: false
          }
        });
        return of([]);
      })
    );
  }

  /**
   * Fetches the areas of interest within the current viewport bounds.
   * If bounds are not defined, it resets the areas of interest and
   * updates the loading state.
   *
   * @param {StateContext<MapStateModel>} ctx The state context containing the map state model.
   * @return {Observable<any[]>} An observable that emits the areas of interest in the viewport bounds.
   */
  private fetchViewportAreas(ctx: StateContext<MapStateModel>): Observable<AreaOfInterest[]> {
    const state = ctx.getState();
    const {bounds} = state.viewport;

    if (!bounds) {
      ctx.patchState({
        areasOfInterest: [],
        isLoading: {
          ...state.isLoading,
          areas: false
        }
      });
      return of([]);
    }
    return this.assetService.getAreasInViewport(bounds).pipe(
      tap(areas => {
        ctx.patchState({
          areasOfInterest: areas,
          isLoading: {
            ...state.isLoading,
            areas: false
          }
        });
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message,
          isLoading: {
            ...state.isLoading,
            areas: false
          }
        });
        return of([]);
      })
    );
  }
}
